import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import {
  NavigationBarType,
  NavigationItemLabelVariant,
  NavigationPosition,
  NavigationResolution,
} from '@unifyapps/defs/types/interface';
import { clsx } from 'clsx';
import type { CSSProperties, RefObject } from 'react';
import {
  DEFAULT_NAVIGATION_ITEM_APPEARANCE,
  EXPANDED_SIDE_NAV_OVERLAY_OFFSET_LEFT,
  EXPANDED_SIDE_NAV_OVERLAY_OFFSET_LEFT_VALUE,
} from '../components/NavigationContainer/constants';
import { InterfaceModes } from '../no-code/stores/InterfaceStore';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- this is a helper class
export class NotificationsHelper {
  static getStylesFromElement = (
    sideNavElement: HTMLDivElement,
    navigationContainerElement: HTMLDivElement,
    isExpandable: boolean,
  ) => {
    // Get width from the side nav
    const rect = sideNavElement.getBoundingClientRect();

    // Get margin from the navigation container
    const computedStyle = window.getComputedStyle(navigationContainerElement);

    const width = rect.width;
    const height = rect.height;
    const marginLeft = parseFloat(computedStyle.marginLeft);
    const marginTop = parseFloat(computedStyle.marginTop);
    const marginBottom = parseFloat(computedStyle.marginBottom);

    // Calculate start offset for the drawer
    const drawerStartOffset =
      (isExpandable ? EXPANDED_SIDE_NAV_OVERLAY_OFFSET_LEFT_VALUE : width) + marginLeft;

    return { drawerStartOffset, marginTop, marginBottom, height };
  };

  static getDrawerStyles = (
    interfaceRecord: InterfaceEntity | undefined,
    resolution: NavigationResolution,
    extraTopOffset?: number,
    sideNavRef?: RefObject<HTMLDivElement | null>,
    navigationContainerRef?: RefObject<HTMLDivElement | null>,
    borderProps?: { className: string; style: CSSProperties | undefined },
    isTopPositioned?: boolean,
  ) => {
    const navigationAppearance = interfaceRecord?.properties.navigation.appearance?.[resolution];
    const navigationItem =
      navigationAppearance?.navigationItem ?? DEFAULT_NAVIGATION_ITEM_APPEARANCE;
    const navigationBar = navigationAppearance?.navigationBar;
    const isExpandable = navigationItem.labelVariant === NavigationItemLabelVariant.Expandable;
    const isFloating = navigationBar?.type === NavigationBarType.Floating;
    const position = navigationBar?.position;

    // No notification panel shown in this cases
    if (
      resolution === NavigationResolution.SM ||
      (position !== NavigationPosition.Start && !isTopPositioned)
    ) {
      return {};
    }

    // Get width from the side nav
    const sideNavElement = sideNavRef?.current;

    // Get margin from the navigation container
    const navigationContainerElement = navigationContainerRef?.current;

    if (!sideNavElement || !navigationContainerElement) {
      return {};
    }

    const { drawerStartOffset, marginBottom, marginTop, height } = this.getStylesFromElement(
      sideNavElement,
      navigationContainerElement,
      isExpandable,
    );

    // Top style  given to accommodate the Interface builder Header height and Top Navigation Bar height
    // Extra 1 px is added to accommodate the border of the navigation bar
    return {
      root: {
        style: {
          ...(!isTopPositioned
            ? { left: `${drawerStartOffset}px` }
            : { top: `${(extraTopOffset ?? 0) + height}px` }),
        },
      },
      ...(isTopPositioned
        ? {
            content: {
              style: {
                top: `${(extraTopOffset ?? 0) + height + marginTop + 1}px`,
              },
            },
            backdrop: {
              style: {
                top: `${(extraTopOffset ?? 0) + height + marginTop + 1}px`,
              },
            },
          }
        : {}),
      container: {
        style: {
          marginTop: isTopPositioned ? 0 : `${marginTop}px`,
          marginBottom: isTopPositioned ? 0 : `${marginBottom}px`,
          overflow: 'hidden',
          // Add border styles only if Floating
          ...(isFloating ? { ...borderProps?.style } : {}),
          ...(isTopPositioned
            ? {
                borderTopWidth: '0px',
                height: `calc(100% - ${(extraTopOffset ?? 0) + height + marginTop + 1}px)`,
              }
            : {}),
        },
        className: clsx(
          { [borderProps?.className ?? '']: isFloating },
          { '!rounded-none': isTopPositioned },
        ),
      },
    } as Record<string, { className?: string; style?: CSSProperties }>;
  };

  static getDrawerStylesForCustomNavigation = ({
    isTopPositioned,
    drawerStartOffset,
    extraTopOffset,
    height,
    position = NavigationPosition.Start,
    drawerHeight,
    mode,
  }: {
    isTopPositioned: boolean;
    drawerStartOffset?: number;
    extraTopOffset: number;
    height: number;
    position?: NavigationPosition;
    drawerHeight?: number;
    mode: InterfaceModes;
  }) => {
    return {
      root: {
        style: {
          ...(!isTopPositioned
            ? { left: `${drawerStartOffset}px` }
            : {
                top: `${(extraTopOffset ?? 0) + height}px`,
                ...(mode !== InterfaceModes.RUNNER
                  ? { position: 'absolute', height: `${drawerHeight}px`, width: '100%' }
                  : {}),
              }),
        },
      },
      ...(isTopPositioned
        ? {
            content: {
              style: {
                ...(mode === InterfaceModes.RUNNER
                  ? { top: `${(extraTopOffset ?? 0) + height}px` }
                  : {
                      height: `${drawerHeight}px`,
                      position: 'absolute',
                    }),
              },
            },
            backdrop: {
              style: {
                top: `${(extraTopOffset ?? 0) + height}px`,
              },
            },
          }
        : {}),
      container: {
        style: {
          overflow: 'hidden',
          // Add border styles only if Floating
          ...(isTopPositioned
            ? {
                ...(mode === InterfaceModes.RUNNER
                  ? {
                      height: `calc(100% - ${(extraTopOffset ?? 0) + height}px)`,
                      borderTopWidth: '0px',
                    }
                  : {
                      height: `${drawerHeight}px`,
                      position: 'absolute',
                      borderTopWidth: '0px',
                    }),
              }
            : {}),
        },
        className: clsx({ '!rounded-none': isTopPositioned }),
      },
      backdrop: {
        style: {
          ...(position === NavigationPosition.Start
            ? { width: `calc(100vw - ${drawerStartOffset}px)` }
            : {}),
          ...(position === NavigationPosition.Top
            ? {
                ...(mode === InterfaceModes.RUNNER
                  ? {
                      height: `calc(100% - ${(extraTopOffset ?? 0) + height} px)`,
                      top: `${(extraTopOffset ?? 0) + height}px`,
                    }
                  : {
                      height: `${drawerHeight}px`,
                      position: 'absolute',
                      width: '100%',
                    }),
              }
            : {}),
        },
      },
    } as Record<string, { className?: string; style?: CSSProperties }>;
  };

  static getNotificationDrawerSlotProps = (
    drawerProps?: Record<string, { className?: string; style?: CSSProperties }>,
    isTopPositioned?: boolean,
  ) => {
    const rootStyles = drawerProps?.root?.style;
    const contentStyles = drawerProps?.content?.style;
    const containerStyles = drawerProps?.container?.style;
    const containerClassName = drawerProps?.container?.className;
    const backdropStyles = drawerProps?.backdrop?.style;

    return {
      root: {
        className: clsx('isolate h-full', {
          [`absolute ${EXPANDED_SIDE_NAV_OVERLAY_OFFSET_LEFT}`]: !isTopPositioned,
        }),
        style: {
          width: '440px',
          ...rootStyles,
        },
      },
      content: {
        className: clsx('h-full  bg-primary !shadow-none ', {
          'relative w-full': !isTopPositioned,
          'w-[440px]': isTopPositioned,
        }),
        style: contentStyles,
      },
      backdrop: {
        className: clsx(' w-screen', { absolute: !isTopPositioned }),

        style: backdropStyles,
      },
      container: { style: containerStyles, className: containerClassName },
    };
  };
}
